import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { collection, addDoc, updateDoc, doc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  Box,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Grid,
} from "@mui/material";
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

// Fonctions utilitaires pour les validations
const isValidEmail = (email) =>
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

const NewStudentForm = ({ onClose, onSubmit, students, subjects, teachers, groups }) => {
  const { t } = useTranslation();
  const [studentData, setStudentData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    fullyRegistered: false,
    adminValidated: true,
    enrollments: [],
    disabled: false,   
    entryDate: new Date().toISOString(),
    startDate: new Date().toISOString().slice(0, 10), 
    endDate: ""
  });

  const [error, setError] = useState(''); 
  const [disabledButton, setDisabledButton] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(""); // Niveau sélectionné
  const [filteredSubjects, setFilteredSubjects] = useState([]); // Matières filtrées
  const [filteredTeachers, setFilteredTeachers] = useState([]); // Enseignants filtrés
  const [filteredGroups, setFilteredGroups] = useState([]); // Groupes filtrés
  const [selectedSubject, setSelectedSubject] = useState(""); // Matière sélectionnée
  const [selectedTeacher, setSelectedTeacher] = useState(""); // Enseignant sélectionné
  const [selectedGroup, setSelectedGroup] = useState(""); // Groupe sélectionné

  // Validation des données avant l'envoi
  const validateStudentData = () => {
    if (!studentData.firstName.trim()) {
      setError(t("First name is required"));
      return false;
    }
    if (!studentData.lastName.trim()) {
      setError(t("Last name is required"));
      return false;
    }

    if (!studentData.email.trim()) {
      setError(t("Email is required"));
      return false;
    }

    // Check if email contains uppercase characters
    if (studentData.email !== studentData.email.toLowerCase()) {
      setError(t("Email must contain only lowercase letters"));
      return false;
    }

    if (!isValidEmail(studentData.email)) {
      setError(t("Please enter a valid email"));
      return false;
    }

    if (!studentData.phone.trim()) {
      setError(t("Phone number is required"));
      return false;
    }

    const isDuplicateEmail = (students || []).some(
      (s) => (s.email || "").toLowerCase() === (studentData.email || "").toLowerCase()
    );
    if (isDuplicateEmail) {
      setError(t("A student with the same email already exists"));
      return false;
    }

    if ((studentData.enrollments || []).length === 0) {
      setError(t("Student must be enrolled in at least one group"));
      return false;
    }

    return true;
  };

  // Filtrer les matières en fonction du niveau sélectionné
  useEffect(() => {
    if (selectedLevel) {
      const filtered = subjects.filter((subject) => subject.level === selectedLevel);
      setFilteredSubjects(filtered);
    } else {
      setFilteredSubjects([]);
    }
    setFilteredTeachers([]);
    setFilteredGroups([]);
    setSelectedSubject("");
    setSelectedTeacher("");
    setSelectedGroup("");
  }, [selectedLevel, subjects]);

  // Filtrer les enseignants en fonction de la matière sélectionnée
  useEffect(() => {
    if (selectedSubject) {
      const filtered = teachers.filter((teacher) =>
        teacher.subjectIds.includes(selectedSubject)
      );
      setFilteredTeachers(filtered);
    } else {
      setFilteredTeachers([]);
    }
    setFilteredGroups([]);
    setSelectedTeacher("");
    setSelectedGroup("");
  }, [selectedSubject, teachers]);

  // Filtrer les groupes en fonction de l'enseignant sélectionné
  useEffect(() => {
    if (selectedTeacher) {
      const filtered = groups.filter(
        (group) =>
          group.teacherId === selectedTeacher &&
          group.level === selectedLevel &&
          group.subjectId === selectedSubject
      );
      setFilteredGroups(filtered);
    } else {
      setFilteredGroups([]);
    }
    setSelectedGroup("");
  }, [selectedTeacher, groups, selectedLevel, selectedSubject]);

  // Ajouter une inscription (enrollment) à la liste
  const handleAddEnrollment = () => {
    if (!selectedGroup) {
      setError(t("Please select a group"));
      return;
    }
  
    // Trouver les détails du groupe sélectionné
    const groupDetails = filteredGroups.find((group) => group.id === selectedGroup);
    if (!groupDetails) {
      setError(t("Invalid group"));
      return;
    }
  
    // Vérifier si l'étudiant est déjà inscrit dans ce groupe
    const isAlreadyEnrolled = studentData.enrollments.some(
      (enrollment) => enrollment.groupId === selectedGroup
    );
  
    if (isAlreadyEnrolled) {
      setError(t("Student is already enrolled in this group"));
      return;
    }
  
    // Ajouter le groupe à la liste des inscriptions avec son nom
    setStudentData((prev) => ({
      ...prev,
      enrollments: [
        ...prev.enrollments,
        {
          groupId: selectedGroup,
          groupName: groupDetails.name, // Inclure le nom du groupe
          subjectId: selectedSubject,
          teacherId: selectedTeacher,
          entryDate: new Date().toISOString(),
        },
      ],
    }));
  
    // Réinitialiser la sélection du groupe
    setSelectedGroup("");
  };
  
  // Gestion de l'envoi du formulaire
  const handleSubmit = async () => {
    if (!validateStudentData()) return;
    setDisabledButton(true)
    
    try {
      const newStudentRef = await addDoc(collection(db, "students"), studentData);

      // Mettre à jour les groupes pour inclure l'ID de l'étudiant
      const groupUpdatePromises = studentData.enrollments.map((enrollment) =>
        updateDoc(doc(db, "groups", enrollment.groupId), {
          studentIds: [
            ...(groups.find((group) => group.id === enrollment.groupId)?.studentIds || []),
            newStudentRef.id,
          ],
        })
      );

      await Promise.all(groupUpdatePromises);

      onSubmit({ id: newStudentRef.id, ...studentData });

      resetForm();
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'étudiant :", error);
      setError(t("An error occurred. Please try again."));
    }
  };

  const resetForm = () => {
    setDisabledButton(false);
    setStudentData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      fullyRegistered: false,
      adminValidated: true,
      enrollments: [],
      disabled: false,
      entryDate: new Date().toISOString(),
      startDate: new Date().toISOString().slice(0, 10),
      endDate: "", 
    });
    setSelectedLevel("");
    setFilteredSubjects([]);
    setFilteredTeachers([]);
    setFilteredGroups([]);
    setSelectedSubject("");
    setSelectedTeacher("");
    setSelectedGroup("");
  };

  return (
    <Box>
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
          {t("Add new student")}
        </Typography>
      </Box>
      {error && (
        <Typography sx={{ color: "red", display: "flex", justifyContent: "center" }}>
          {error}
        </Typography>
      )}
      <Box>
        <TextField
          label={t("First name")}
          fullWidth
          value={studentData.firstName}
          onChange={(e) => setStudentData({ ...studentData, firstName: e.target.value })}
          sx={{marginBottom: '20px'}}
        />
        <TextField
          label={t("Last name")}
          fullWidth
          value={studentData.lastName}
          onChange={(e) => setStudentData({ ...studentData, lastName: e.target.value })}
          sx={{marginBottom: '20px'}}
        />
        <TextField
          label={t("Email")}
          fullWidth
          value={studentData.email}
          onChange={(e) => setStudentData({ ...studentData, email: e.target.value })}
          sx={{marginBottom: '20px'}}
        />
        <TextField
          label={t("Phone")}
          fullWidth
          value={studentData.phone}
          onChange={(e) => setStudentData({ ...studentData, phone: e.target.value })}
          sx={{marginBottom: '20px'}}
        />

        {/* Start / End Dates */}
        <Grid container spacing={2} sx={{ mb: 2 }} dir="rtl">
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("Start date")}
              type="date"
              fullWidth
              value={studentData.startDate || ""}
              onChange={(e) => setStudentData({ ...studentData, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText={t("Select subscription start date")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("End date")}
              type="date"
              fullWidth
              value={studentData.endDate || ""}
              onChange={(e) => setStudentData({ ...studentData, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText={t("Select subscription end date (optional)")}
            />
          </Grid>
        </Grid>

        <FormControl fullWidth sx={{marginBottom: '10px'}}>
          <Typography>{t("Level")}</Typography>
          <Select value={selectedLevel} onChange={(e) => setSelectedLevel(e.target.value)}>
            <MenuItem value="">{t("Select level")}</MenuItem>
            {/* Extraire les niveaux uniques */}
            {[...new Set(subjects.map((subject) => subject.level))].map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{marginBottom: '10px'}}>
          <Typography>{t("Subject")}</Typography>
          <Select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
          >
            <MenuItem value="">{t("Select subject")}</MenuItem>
            {filteredSubjects.map((subject) => (
              <MenuItem key={subject.id} value={subject.id}>
                {subject.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{marginBottom: '10px'}}>
          <Typography>{t("Teacher")}</Typography>
          <Select
            value={selectedTeacher}
            onChange={(e) => setSelectedTeacher(e.target.value)}
          >
            <MenuItem value="">{t("Select teacher")}</MenuItem>
            {filteredTeachers.map((teacher) => (
              <MenuItem key={teacher.id} value={teacher.id}>
                {`${teacher.firstName} ${teacher.lastName}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl fullWidth sx={{marginBottom: '10px'}}>
          <Typography>{t("Group")}</Typography>
          <Select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
          >
            <MenuItem value="">{t("Select group")}</MenuItem>
            {filteredGroups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </Select>

          <Button 
            variant="contained" 
            color="success" 
            onClick={handleAddEnrollment} 
            sx={{marginTop: '20px', fontSize:'18px'}} 
            disabled={ !selectedLevel || !selectedSubject || !selectedTeacher || !selectedGroup}>

            {t("Add to group")}

          </Button>

        </FormControl>

        <Alert severity="info"> 
          <AlertTitle fontSize='17px'><b>{t("Current enrollments")} </b></AlertTitle>
          {studentData.enrollments.map((enrollment, index) => (
            <Typography key={index}>{t("Group")}: {enrollment.groupName}</Typography>
            ))}
        </Alert>


      </Box>
      <Box
        sx={{
          position: "flex",
          bottom: 0,
          mt: 2,
          pt: 1.5,
          bgcolor: "white",
          borderTop: "1px solid rgba(15, 23, 42, 0.08)",
        }}
      >
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          fullWidth
          disabled={(selectedLevel && selectedSubject && selectedTeacher && selectedGroup) || disabledButton}
          sx={{ fontWeight: 700 }}
        >
          {t("Save")}
        </Button>
      </Box>
    </Box>
  );
};

export default NewStudentForm;
